import { type FC, type ReactNode } from "react";
import {
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Skeleton,
} from "@mui/material";
import {
  SimpleTable,
  type SimpleTableColumn,
} from "@/partner/shared/components/SimpleTable";
import { ChangeLimit } from "@/partner/shared/components/ChangeLimit";
import { ErrorMessage } from "@/shared/components";

interface SimpleTableCardProps {
  title: string;
  subheader?: string;
  columns: SimpleTableColumn[];
  rows: unknown[][];
  loading?: boolean;
  /**
   * Ошибка от useQuery этого виджета. Виджет с ошибкой обязан показать её
   * явно, а не выглядеть как «нет данных» (спека, раздел 5) — и не должен
   * ломать соседние виджеты вкладки, поэтому ошибка рендерится внутри
   * карточки, а не пробрасывается наверх.
   */
  error?: unknown;
  minHeight?: number;
  // Опциональны: таблицы без пагинации (фиксированные небольшие наборы,
  // напр. разбивка по типу устройства) не рендерят CardActions/ChangeLimit
  // вовсе — см. план Task 22.
  limit?: number;
  setLimit?: (limit: number) => void;
  downloadButton?: ReactNode;
}

export const SimpleTableCard: FC<SimpleTableCardProps> = ({
  title,
  subheader,
  columns,
  rows,
  loading,
  error,
  minHeight = 350,
  limit,
  setLimit,
  downloadButton,
}) => {
  if (error) {
    return (
      <Card sx={{ width: "100%" }}>
        <CardHeader title={title} subheader={subheader} />
        <CardContent sx={{ pt: 0 }}>
          <ErrorMessage error={error} />
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card sx={{ width: "100%" }}>
        <CardHeader title={title} subheader={subheader} />
        <CardContent sx={{ pt: 0 }}>
          <Skeleton variant="rectangular" height={minHeight} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ width: "100%" }}>
      <CardHeader title={title} subheader={subheader} action={downloadButton} />
      <SimpleTable columns={columns} rows={rows} />
      {limit !== undefined && setLimit !== undefined && (
        <CardActions sx={{ justifyContent: "end" }}>
          <ChangeLimit limit={limit} setLimit={setLimit} />
        </CardActions>
      )}
    </Card>
  );
};
